import {HttpErrorResponse} from '@angular/common/http';
import {inject, Injectable} from '@angular/core';
import {Router} from '@angular/router';
import {OAuthErrorEvent, OAuthService, OAuthSuccessEvent} from 'angular-oauth2-oidc';
import {JwksValidationHandler} from 'angular-oauth2-oidc-jwks';
import {Observable, Subject, Subscription} from 'rxjs';
import {OrganisationData} from '../../content/interfaces/organisation/organisation-data';
import {UserData} from '../../content/interfaces/user-data';
import {ZbvLoginStatus} from '../enums/zbv-login-status';
import {RuntimeConfigService} from './runtime-config.service';

@Injectable({
  providedIn: 'root'
})
export class AuthService {

  /**
   * active Organisation
   */
  organisationData: OrganisationData;

  /**
   * user roles from zbv-jwt as string-array
   * and userData of the user who is logged in
   */
  userRoles: string[] = [];
  userData: UserData;

  /**
   * Auth params
   */
  private loginOk= new Subject<boolean>();
  login: Observable<boolean> = this.loginOk.asObservable();
  isLoggedIn: boolean;
  zbvLoginStatus: ZbvLoginStatus = ZbvLoginStatus.NONE;
  hasValidToken: boolean = false;
  idToken: string = '';
  accessToken: string;
  oAuthSubscription: Subscription;

  /**
   * OAuth-Service from angular-oauth2-oidc
   * configs and flags for authentification
   */
  oAuthService = inject(OAuthService);

  constructor(private router: Router,
              private runtimeConfig: RuntimeConfigService) { }

  /**
   * Check sso-login (valid token) or perform ZBV-Login
   */
  performZbvLogin() {

    this.oAuthService.configure(this.runtimeConfig.provideOAuthConfig());
    this.oAuthService.tokenValidationHandler = new JwksValidationHandler();
    if (this.oAuthService.hasValidAccessToken()) {
      this.hasValidToken = true;
      this.idToken = this.oAuthService.getIdToken();
      this.accessToken = this.oAuthService.getAccessToken();
      this.loginOk.next(true);
    }
    /**
     * Subscribe for Token-Refresh-Event
     */
    this.oAuthSubscription = this.oAuthService.events.subscribe(e => {
      if (e instanceof OAuthSuccessEvent) {
        if (e.type === 'token_received') {
          if (this.oAuthService.hasValidAccessToken()) {
            this.hasValidToken = true;
            this.idToken = this.oAuthService.getIdToken();
            this.accessToken = this.oAuthService.getAccessToken();
            /**
             * If this is the initial token retrieval and not an automatic token refresh,
             * then LoginOK must be triggered.
             * During a token refresh—if the automatic refresh option is enabled
             * (see below -> this.oauthService.setupAutomaticSilentRefresh();)—
             * LoginOK must NOT be triggered, otherwise the app would always route
             * back to the start component.
             */
            if (!this.isLoggedIn) {
              this.loginOk.next(true);
            }
          }
        }
      }
      /**
       * oAuth Error-Event was triggered
       */
      if (e instanceof OAuthErrorEvent) {
        console.log('OAuth-Error-Event: Routing to logout', e);
        /**
         * Auf global logout prüfen und APO Logout auslösen
         * bzw. Fehlermeldung ausgeben, falls kein global logout
         */
        if (e.type === 'token_refresh_error') {
          if (e.reason instanceof HttpErrorResponse) {
            if (e.reason.error.error === 'invalid_grant') {
              this.zbvLoginStatus = ZbvLoginStatus.NONE;
              /** Route to Logout, see APO
                * set error-specific information-params for logout
                * APO: this.router.navigate(['logout'], {replaceUrl: true});
                */
              this.hasValidToken = false;
              this.logoutZBV();
            } else {
              /**
               * set error-specific information-params
               */
              this.routeOnZbvError('auth');
              /**
                * this.errorHandler.hasError = true;
                * this.errorHandler.msgTitle = 'ZBV Authentifikations-Fehler';
                * this.errorHandler.msgBody = '<strong>Es ist ein Fehler bei der ZBV Authentifikation aufgetreten.</strong><br><i>' + e.reason.status + '<br>' + e.reason.message + '</i><br><strong>Bitte wenden Sie sich an den Support.</strong>';
                * this.router.navigate(['logout/error'], {replaceUrl: true});
               */
            }
          } else {
            /**
             * set error-specific information-params
             */
            this.routeOnZbvError('auth');
            /**
              * this.errorHandler.hasError = true;
              * this.errorHandler.msgTitle = 'ZBV Authentifikations-Fehler';
              * this.errorHandler.msgBody = '<strong>Es ist ein Fehler bei der ZBV Authentifikation aufgetreten.</strong><br><i>' + e.type + '</i><br><strong>Bitte wenden Sie sich an den Support.</strong>';
              * this.router.navigate(['logout/error'], {replaceUrl: true});
              */
          }
        } else {
          /**
           * set error-specific information-params
           */
          this.routeOnZbvError('auth');
          /*
          this.errorHandler.hasError = true;
          this.errorHandler.msgTitle = 'ZBV Authentifikations-Fehler';
          this.errorHandler.msgBody = '<strong>Es ist ein Fehler bei der ZBV Authentifikation aufgetreten.</strong><br><i>' + e.type + '</i><br><strong>Bitte wenden Sie sich an den Support.</strong>';
          this.router.navigate(['logout/error'], {replaceUrl: true});
           */
        }
      }
    });

    /**
     * Check for a valid access-token
     * and call login
     */
    if (!this.hasValidToken) {
      this.oAuthService.loadDiscoveryDocumentAndLogin({disableOAuth2StateCheck: true}).then(result => {
        // Ok
      }).catch(error => {
        this.routeOnZbvError('auth');
      });
    } else {
      /**
       * in case of  F5 browser-refresh -> Discovery-Document must always be loaded, for links and logout...
       */
      this.oAuthService.loadDiscoveryDocumentAndTryLogin({disableOAuth2StateCheck: true}).then(result => {
      }).catch(error => {
      });
    }

    /**
     * GLOBAL LOGOUT
     * If a logout in ZBV is intended to also trigger a logout in GC,
     * then silent refresh must be enabled.
     * If no logout should be triggered, the following line of code
     * must be disabled.
     */
    this.oAuthService.setupAutomaticSilentRefresh();

  }

  /**
   * Routing to error-page in case of zbv error or logout
   * @param routeEndpoint
   * @param msTitle
   * @param msgBody
   * @private
   */
  private routeOnZbvError(routeEndpoint: 'auth' | 'logout', msTitle: string = '', msgBody: string = ''): void {
    if (routeEndpoint === 'auth') {
      this.isLoggedIn = false;
      this.zbvLoginStatus = ZbvLoginStatus.ERROR;
    } else {
      this.zbvLoginStatus = ZbvLoginStatus.LOGOUT;
    }
    this.router.navigate(['/error/' + routeEndpoint], {replaceUrl: true});
  }

  /**
   * ZBV claims from Keycloak-Server
   */
  getZbvClaims() {
    let claims: any = this.oAuthService.getIdentityClaims();
    return claims ? claims : null;
  }

  /**
   * Performs a complete ZBV logout, including local cleanup
   * and forwarding logout parameters required by ZBV.
   */
  logoutZBV(): void {

    /**
     * IMPORTANT NOTES ABOUT LOGOUT PARAMETERS
     *
     * - The `client_id` must be included as a query parameter when performing
     *   a logout against ZBV (as required by the ZBV compendium),
     *   even though Keycloak normally requires it only when `id_token_hint`
     *   is NOT provided.
     *
     * - We intentionally avoid sending an `id_token_hint`.
     *   Reason:
     *     ZBV issues very large id_tokens containing all roles.
     *     Adding such a token as a query parameter produces URLs that exceed
     *     browser limits, resulting in “URI too long” errors.
     *
     * - Clearing `sessionStorage` ensures that `id_token_hint` is NOT
     *   appended automatically by the OAuth library.
     *   This avoids oversized logout URLs.
     */

    // Remove all stored token data and prevent automatic id_token_hint
    sessionStorage.clear();

    // Remove the current page from the browser's navigation history
    // to prevent the user from navigating back into the protected area.
    history.pushState(null, '');

    /**
     * Trigger the ZBV logout via OAuthService.
     * Only the client_id is passed, as required by ZBV.
     */
    this.oAuthService.logOut({
      client_id: this.oAuthService.clientId
    });
  }

  /**
   * Mapping for the user-data of the current user after ZBV-Login
   */
  initCurrentUserData(userData: UserData) {
    this.userData = userData;
    this.userData.avatar = '';
    if (this.userData?.firstName && this.userData?.firstName.length > 0) {
      this.userData.avatar = this.userData.firstName.slice(0,1);
    }
    if (this.userData?.lastName && this.userData?.lastName.length > 0) {
      this.userData.avatar += this.userData.lastName.slice(0,1);
    }
    if (this.userData.avatar === '') {
      this.userData.avatar = 'na';
    }
  }

  /**
   * check for single user role
   * @param roleName
   */
  userHasRole(roleName: string): boolean {
    return this.userRoles.includes(roleName);
  }

  /**
   * check if user has any of the roles in the list
   * @param roleNameList
   */
  userHasRoleInList(roleNameList: string[]): boolean {
    return roleNameList.some(role => this.userRoles.includes(role));
  }

}
